/**
 * App — router entry point and authentication lifecycle manager.
 *
 * Responsibilities:
 *  - Session validation on every page load (getMe via httpOnly cookie).
 *  - Dev-bypass auto-login when REACT_APP_DEV_BYPASS_AUTH=true.
 *  - OAuth callback fragment parsing: sets auth state then navigates to /app.
 *  - Subscription refresh whenever the authenticated user changes.
 *  - Renders the client-side route tree.
 *
 * Route structure:
 *  /                         → LandingRoute  (AuthLanding + policy/info overlays)
 *  /app                            → AppShell → DashboardPage
 *  /app/project/:projectId         → AppShell → ProjectDataLoader → ProjectPage
 *  /app/project/:projectId/insights → AppShell → ProjectDataLoader → InsightsPage
 *  /app/project/:projectId/plan     → AppShell → ProjectDataLoader → PlanPage
 *  *                               → LandingRoute  (fallback)
 *
 * ProjectDataLoader fetches and hydrates a project's data once and gates
 * its nested route on that load — see src/hooks/useProjectData.ts.
 *
 * Must be rendered inside <BrowserRouter> provided by index.tsx.
 */
import { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useStore } from './store/useStore';
import { getMe } from './api/auth';
import { isDevBypassEnabled, fetchDevAuth } from './utils/devAuth';
// Initialise the feature registry before any component renders.
import './pricing/features';
import './styles/app.css';
import './styles/landing.css';
import AuthLanding from 'components/pages/AuthLanding';
import { CookieConsent, CONSENT_KEY, initialiseMixpanel } from 'components/ui/CookieConsent';
import Tooltip from 'components/ui/Tooltip';
import mixpanel from 'mixpanel-browser';
import PolicyPage from 'components/pages/PolicyPage';
import PhilosophyPage from 'components/pages/PhilosophyPage';
import PricingPage from 'components/pages/PricingPage';
import AppShell from 'components/pages/AppShell';
import DashboardPage from 'components/pages/DashboardPage';
import ProjectDataLoader from 'components/pages/ProjectDataLoader';
import ProjectPage from 'components/pages/ProjectPage';
import InsightsPage from 'components/pages/InsightsPage';
import PlanPage from 'components/pages/PlanPage';

// ─── Landing Route ────────────────────────────────────────────────────────────

/**
 * Self-contained landing route.
 * Wraps AuthLanding with policy/info page overlays and exposes a "Proceed to App"
 * CTA when the user is already signed in.
 */
function LandingRoute() {
    const navigate = useNavigate();
    const user = useStore((s) => s.user);

    const [authError, setAuthError] = useState('');
    const [policyPage, setPolicyPage] = useState<'privacy' | 'terms' | 'impressum' | null>(null);
    const [infoPage, setInfoPage] = useState<'philosophy' | 'pricing' | null>(null);

    // Parse auth_error surfaced in the query string after a failed OAuth redirect.
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const urlError = queryParams.get('auth_error');
        if (urlError) {
            setAuthError(decodeURIComponent(urlError));
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    const handleLinkedInSignIn = () => {
        const apiBase = process.env.REACT_APP_API_BASE_URL || '';
        window.location.href = `${apiBase}/api/auth/linkedin`;
    };

    const handleGoogleSignIn = () => {
        const apiBase = process.env.REACT_APP_API_BASE_URL || '';
        window.location.href = `${apiBase}/api/auth/google`;
    };

    // Overlay early returns keep the existing UX where policy/info pages replace the landing.
    if (policyPage) {
        return <PolicyPage page={policyPage} onBack={() => setPolicyPage(null)} />;
    }
    if (infoPage === 'philosophy') {
        return <PhilosophyPage onBack={() => setInfoPage(null)} />;
    }
    if (infoPage === 'pricing') {
        return <PricingPage onBack={() => setInfoPage(null)} />;
    }

    return (
        <AuthLanding
            authError={authError}
            onSignIn={handleLinkedInSignIn}
            onGoogleSignIn={handleGoogleSignIn}
            onSelectPolicy={(page) => setPolicyPage(page)}
            onSelectInfoPage={(page) => setInfoPage(page)}
            user={user ?? undefined}
            onProceedToApp={user ? () => navigate('/app') : undefined}
        />
    );
}

// ─── App Component ────────────────────────────────────────────────────────────

/**
 * Root application component. Manages auth lifecycle and renders the route tree.
 * BrowserRouter context is provided by index.tsx.
 */
export default function App() {
    const { user, setAuth, clearAuth, refreshSubscription, refreshAiUsage } = useStore();
    const navigate = useNavigate();

    /** Guard against dev bypass firing more than once per page load. */
    const devBypassAttemptedRef = useRef(false);

    // ── Session Validation ──────────────────────────────────────────────────
    // Validate the httpOnly cookie session on every page load.
    useEffect(() => {
        getMe()
            .then((me) => {
                if (me && !me.error) {
                    setAuth({
                        id: me.id,
                        name: me.name,
                        plan: me.plan as any,
                        linkedinId: me.linkedinId || me.googleId || '',
                    });
                } else {
                    clearAuth();
                }
            })
            .catch(() => { /* Network error — retain existing user state. */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Dev Bypass Auto-Login ────────────────────────────────────────────────
    // Auto sign-in for local development when REACT_APP_DEV_BYPASS_AUTH=true.
    useEffect(() => {
        const devEnabled = isDevBypassEnabled();
        console.log(devEnabled);
        if (!isDevBypassEnabled() || user || devBypassAttemptedRef.current) return;
        devBypassAttemptedRef.current = true;
        fetchDevAuth()
            .then(({ userId, name, linkedinId }) => {
                setAuth({ id: userId, name, linkedinId });
            })
            .catch((err) => {
                console.warn('[Dev bypass] Auto-login failed:', err.message);
            });
    }, [user, setAuth]);

    // ── OAuth Callback Fragment Parsing ──────────────────────────────────────
    // Auth tokens arrive in the URL fragment (#) to keep them out of server logs.
    useEffect(() => {
        const fragment = window.location.hash.startsWith('#')
            ? new URLSearchParams(window.location.hash.slice(1))
            : new URLSearchParams();
        const queryParams = new URLSearchParams(window.location.search);

        const urlName = fragment.get('name');
        const urlUserId = fragment.get('userId');
        const urlLinkedinId = fragment.get('linkedinId');
        const urlGoogleId = fragment.get('googleId');
        const urlPlan = fragment.get('plan');
        const urlUserCategory = fragment.get('userCategory');
        const checkoutStatus = queryParams.get('checkout');

        if (urlName && urlUserId) {
            const plan = urlPlan === 'pro' || urlPlan === 'teams' ? urlPlan : 'free';
            const validCategories = ['standard_user', 'power_user', 'zeytune_team'] as const;
            type ValidCategory = (typeof validCategories)[number];
            const userCategory = validCategories.includes(urlUserCategory as ValidCategory)
                ? (urlUserCategory as ValidCategory)
                : 'standard_user';
            setAuth({
                id: urlUserId,
                name: urlName,
                linkedinId: urlLinkedinId || urlGoogleId || '',
                plan,
                userCategory,
            });
            // Clean the fragment then navigate into the authenticated app.
            window.history.replaceState({}, '', window.location.pathname);
            navigate('/app');
        } else if (checkoutStatus === 'success') {
            refreshSubscription();
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [setAuth, refreshSubscription, navigate]);

    // ── Subscription Refresh ─────────────────────────────────────────────────
    useEffect(() => {
        if (user) refreshSubscription();
    }, [user, refreshSubscription]);

    // ── AI Usage Refresh ──────────────────────────────────────────────────────
    // Loads the current month's AI credit consumption on login/page load. Refreshed
    // again after each individual AI action (see src/pricing/aiUsageLimit.ts) so the
    // displayed count stays current without polling.
    useEffect(() => {
        if (user) refreshAiUsage();
    }, [user, refreshAiUsage]);

    // ── Mixpanel User Identity ───────────────────────────────────────────────
    // Identify the authenticated user for Mixpanel user profiles.
    // Only runs when analytics consent was previously granted so we stay
    // GDPR-compliant — no PII is sent before the user accepts cookies.
    // initialiseMixpanel() is called first because the npm package does not
    // queue pre-init calls; this guarantees init regardless of effect order.
    useEffect(() => {
        const hasConsent = localStorage.getItem(CONSENT_KEY) === 'granted';
        if (!user || !hasConsent) return;
        initialiseMixpanel();
        mixpanel.identify(user.id);
        mixpanel.people.set({
            $name: user.name,
            plan: user.plan ?? 'free',
        });
    }, [user]);

    // ── Route Tree ────────────────────────────────────────────────────────────
    return (
        <>
            <CookieConsent />
            <Tooltip />
            <Routes>
                <Route path='/' element={<LandingRoute />} />
                <Route element={<AppShell />}>
                    <Route path='/app' element={<DashboardPage />} />
                    <Route path='/app/project/:projectId' element={<ProjectDataLoader />}>
                        <Route index element={<ProjectPage />} />
                        <Route path='insights' element={<InsightsPage />} />
                        <Route path='plan' element={<PlanPage />} />
                    </Route>
                </Route>
                <Route path='*' element={<LandingRoute />} />
            </Routes>
        </>
    );
}
